import React, { useEffect, useState } from 'react';
import { Pencil, Save, X } from 'lucide-react';
import type { Workspace } from '../../../types';

interface Props {
  workspace: Workspace | null;
  canEdit: boolean;
  busy: boolean;
  onRename: (name: string) => Promise<boolean>;
}

const WorkspaceHeader: React.FC<Props> = ({ workspace, canEdit, busy, onRename }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(workspace?.name ?? '');

  useEffect(() => {
    setDraft(workspace?.name ?? '');
  }, [workspace?.name]);

  const handleSave = async () => {
    if (!draft.trim()) return;
    if (draft.trim() === workspace?.name) {
      setEditing(false);
      return;
    }
    const ok = await onRename(draft);
    if (ok) setEditing(false);
  };

  const handleCancel = () => {
    setDraft(workspace?.name ?? '');
    setEditing(false);
  };

  if (!workspace) return null;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-white/5 border border-white/10 rounded-xl">
      <div className="flex-1 min-w-0">
        <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Workspace</div>
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={busy}
              maxLength={120}
              className="flex-1 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleSave();
                if (e.key === 'Escape') handleCancel();
              }}
            />
            <button
              type="button"
              onClick={handleSave}
              disabled={busy || !draft.trim()}
              className="p-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg"
              aria-label="Save workspace name"
            >
              <Save className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={busy}
              className="p-1.5 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg"
              aria-label="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="text-lg font-semibold text-white truncate">{workspace.name}</div>
            {canEdit && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="p-1 text-gray-400 hover:text-white"
                aria-label="Rename workspace"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="text-xs text-gray-400 shrink-0">
        {workspace.region ? <span className="mr-3">Region: {workspace.region}</span> : null}
        <span>Created {new Date(workspace.created_at).toLocaleDateString()}</span>
      </div>
    </div>
  );
};

export default WorkspaceHeader;
